<script setup>
import { QuestionMarkCircleIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/outline";
import { DateTime } from "luxon";
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { twMerge } from "tailwind-merge";

import UserCreateDialog from "./UserCreateDialog.vue";

import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, SortButton } from "./ui/table";
import Tooltip from "./ui/Tooltip.vue";
import globals from "../globals";

const { t } = useI18n();

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  value: {
    type: Array,
    required: false,
  },
  onSelect: {
    type: Function,
    required: true,
  },
  onCreate: {
    type: Function,
  },
});

const sortBy = ref("Name");
const sortDesc = ref(false);
const isCollapsed = ref(false);

function handleSortChange(column) {
  if (sortBy.value === column) {
    sortDesc.value = !sortDesc.value;
    return;
  }

  sortBy.value = column;
  sortDesc.value = false;
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}

const sortedUsers = computed(() => {
  let sorted = [...props.options].sort(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  return sortDesc.value ? sorted.reverse() : sorted;
});

const isCreateDialogOpen = ref(false);
function handleToggleCreateDialog() {
  isCreateDialogOpen.value = !isCreateDialogOpen.value;
}

const userForm = ref(null);
const hasUserNameBeenModified = ref(false);

watch(() => {
  if (!hasUserNameBeenModified.value && userForm.value?.Name) {
    userForm.value.UserName = userForm.value.Name.toLowerCase().split(" ").join(".");
  }
});

function handleCreate([userId, err]) {
  if (err) {
    props.onCreate?.([null, err]);
    return;
  }

  props.onSelect(userId);
  props.onCreate?.([userId, err]);
}
</script>

<template>
  <div class="flex flex-col space-y-2">
    <div class="inline-flex items-center">
      <button
        class="flex cursor-pointer scroll-m-20 items-center gap-2 rounded-sm text-2xl font-semibold tracking-tight transition-colors hover:text-primary"
        @click="toggleCollapse"
        @keydown.enter="toggleCollapse"
        @keydown.space.prevent="toggleCollapse"
        :aria-expanded="!isCollapsed"
        aria-controls="users-table-section"
        type="button"
      >
        <component :is="isCollapsed ? ChevronDownIcon : ChevronUpIcon" class="h-6 w-6" aria-hidden="true" />
        <span>{{ t("pages.wizard.steps.users.users.heading") }}</span>
      </button>

      <Tooltip
        aria-label="SAML users"
        :focusable="true"
        :text="t('pages.wizard.steps.users.users.tooltip')"
        class="ml-2"
      >
        <QuestionMarkCircleIcon class="h-6 w-6 text-muted-foreground" />
      </Tooltip>
    </div>

    <div v-show="!isCollapsed" id="users-table-section" class="flex flex-col space-y-2 transition-all duration-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <span class="sr-only">{{ t("common.select") }}</span>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'Name' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('Name')">
                {{ t("pages.wizard.steps.users.users.table.columns.Name") }}
              </SortButton>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'Company' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('Company')">
                {{ t("pages.wizard.steps.users.users.table.columns.Company") }}
              </SortButton>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'UserName' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('UserName')">
                {{ t("pages.wizard.steps.users.users.table.columns.UserName") }}
              </SortButton>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'LastLogin' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('LastLogin')">
                {{ t("pages.wizard.steps.users.users.table.columns.LastLogin") }}
              </SortButton>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-if="options.length === 0">
            <TableCell colspan="5">{{ t("pages.wizard.steps.users.users.empty.title") }}</TableCell>
          </TableRow>
          <TableRow
            v-for="user in sortedUsers"
            :key="user.ID"
            :data-testid="`user-${user.ID}`"
            class="cursor-pointer"
            @click="(e) => !e.target.closest('label') && onSelect(user.ID)"
            @change="onSelect(user.ID)"
          >
            <TableCell>
              <label class="relative flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  name="user-select"
                  class="peer sr-only"
                  :value="user.Name"
                  :checked="value.includes(user.ID)"
                  :aria-label="`Select user '${user.Name}'`"
                />

                <span
                  aria-hidden="true"
                  :class="
                    twMerge(
                      'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                      value.includes(user.ID) &&
                        'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                    )
                  "
                />
              </label>
            </TableCell>
            <TableCell>
              {{ user.Name }}
            </TableCell>
            <TableCell>
              {{ user.Company || "-" }}
            </TableCell>
            <TableCell>
              {{ user.UserName }}
            </TableCell>
            <TableCell>
              {{ DateTime.fromISO(user.LastLogin).toRelative() || "-" }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <div>
        <button
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          @click="handleToggleCreateDialog"
        >
          {{ t("pages.wizard.steps.users.users.actions.create") }}
        </button>
      </div>
    </div>
  </div>

  <UserCreateDialog :show="isCreateDialogOpen" @toggle-show="handleToggleCreateDialog" @save="handleCreate" />
</template>
